import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, X, Pencil, Trash2, FileText, Copy, Utensils, Trash } from "lucide-react";

const MEAL_TYPES = [
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "tea", label: "Tea / Snacks" },
  { value: "dinner", label: "Dinner" },
];
const ORDER = { breakfast: 0, lunch: 1, tea: 2, dinner: 3 };

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const formatDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const emptyForm = () => ({ date: todayISO(), meal_type: "lunch", pax: 8, notes: "", items: [] });

export default function StaffMeals() {
  const [meals, setMeals] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new" | meal object
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [dupTarget, setDupTarget] = useState(null); // meal object
  const [dupDate, setDupDate] = useState(todayISO());
  const [dupType, setDupType] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      // meals from staff_meals collection + live menu items to populate the picker
      const [mealsRes, menuRes] = await Promise.all([
        api.get("/staff/staff-meals"),
        api.get("/menu-items"),  // ← LIVE menu (public collection), not a seed
      ]);
      setMeals(mealsRes.data);
      setMenuItems(menuRes.data);
    } catch { toast.error("Could not load staff meals"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Group meals by date desc
  const grouped = useMemo(() => {
    const g = {};
    for (const m of meals) (g[m.date] = g[m.date] || []).push(m);
    for (const arr of Object.values(g)) arr.sort((a, b) => (ORDER[a.meal_type] ?? 9) - (ORDER[b.meal_type] ?? 9));
    return Object.entries(g).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [meals]);

  const openNew = () => { setEditing("new"); setForm(emptyForm()); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      date: m.date, meal_type: m.meal_type, pax: m.pax || 0, notes: m.notes || "",
      items: (m.items || []).map((i) => ({ ...i })),
    });
  };
  const close = () => { setEditing(null); setForm(emptyForm()); };

  const addLineFromMenu = (mi) => {
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        menu_item_id: mi.id || "", name: mi.name, section: mi.section || "",
        portions: 0, notes: "",
      }],
    }));
  };
  const addBlankLine = () => setForm((f) => ({ ...f, items: [...f.items, { menu_item_id: "", name: "", section: "", portions: 0, notes: "" }] }));
  const updateLine = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((ln, idx) => (idx === i ? { ...ln, ...patch } : ln)) }));
  const removeLine = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Pick a date"); return; }
    if (form.items.length === 0) { toast.error("Add at least one item"); return; }
    for (const [i, ln] of form.items.entries()) {
      if (!ln.name.trim()) { toast.error(`Line ${i + 1}: needs a name`); return; }
    }
    setSaving(true);
    try {
      const payload = {
        date: form.date, meal_type: form.meal_type,
        pax: parseInt(form.pax) || 0,
        notes: form.notes,
        items: form.items.map((ln) => ({
          menu_item_id: ln.menu_item_id || "", name: ln.name.trim(),
          section: ln.section || "", portions: Number(ln.portions) || 0,
          notes: ln.notes || "",
        })),
      };
      let saved;
      if (editing === "new") {
        const { data } = await api.post("/staff/staff-meals", payload);
        saved = data;
        setMeals((p) => [saved, ...p]);
      } else {
        const { data } = await api.patch(`/staff/staff-meals/${editing.id}`, payload);
        saved = data;
        setMeals((p) => p.map((m) => (m.id === saved.id ? saved : m)));
      }
      toast.success(`Saved ${MEAL_TYPES.find((t) => t.value === saved.meal_type)?.label} · ${saved.date}`);
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete the ${m.meal_type} plan for ${m.date}?`)) return;
    try {
      await api.delete(`/staff/staff-meals/${m.id}`);
      setMeals((p) => p.filter((x) => x.id !== m.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const openDuplicate = (m) => { setDupTarget(m); setDupDate(addDays(m.date, 1)); setDupType(""); };
  const closeDuplicate = () => { setDupTarget(null); };
  const doDuplicate = async () => {
    try {
      const payload = { to_date: dupDate };
      if (dupType) payload.to_meal_type = dupType;
      const { data } = await api.post(`/staff/staff-meals/${dupTarget.id}/duplicate`, payload);
      setMeals((p) => [data, ...p]);
      toast.success(`Duplicated to ${data.date} · ${data.meal_type}`);
      closeDuplicate();
    } catch (err) { toast.error(err?.response?.data?.detail || "Duplicate failed"); }
  };

  const downloadDayPdf = async (date) => {
    setDownloading(date);
    try {
      const res = await api.get(`/staff/staff-meals/day.pdf`, { params: { date }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-Staff-Meals-${date}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("PDF download failed"); }
    finally { setDownloading(null); }
  };

  return (
    <div data-testid="staff-meals-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Kitchen &amp; team</div>
          <h1 className="mt-2 text-4xl">Staff meals</h1>
          <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] max-w-2xl">
            Plan meals for cooks &amp; workers by date and meal type. Pick items from your live menu, or add
            off-menu basics like rice &amp; dal. Download a daily kitchen handout as PDF.
          </p>
        </div>
        <button data-testid="staff-meal-new" onClick={openNew} className="btn-brass !py-2.5 !px-5 text-sm">
          <Plus size={14} /> New meal plan
        </button>
      </div>

      <div className="mt-8 space-y-8">
        {loading && <div className="p-6 text-[hsl(var(--ink-soft))]">Loading…</div>}
        {!loading && grouped.length === 0 && (
          <div data-testid="staff-meals-empty" className="p-10 text-center text-[hsl(var(--ink-soft))] border border-dashed border-[hsl(var(--rule))]">
            <Utensils className="mx-auto mb-2 opacity-40" size={28} />
            No staff meals planned yet. Click <b>New meal plan</b> to add one.
          </div>
        )}
        {!loading && grouped.map(([date, list]) => (
          <div key={date} data-testid={`meal-day-${date}`}>
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-[hsl(var(--brass-2))]">{formatDate(date)}</div>
                <div className="text-xs text-[hsl(var(--ink-soft))]">{list.length} meal{list.length > 1 ? "s" : ""} · {list.reduce((s, m) => s + (m.pax || 0), 0)} total pax</div>
              </div>
              <button
                data-testid={`meal-day-pdf-${date}`}
                disabled={downloading === date}
                onClick={() => downloadDayPdf(date)}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors disabled:opacity-40"
              >
                <FileText size={12} /> {downloading === date ? "Preparing…" : "Day PDF"}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {list.map((m) => (
                <div key={m.id} data-testid={`meal-${m.id}`} className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold">{MEAL_TYPES.find((t) => t.value === m.meal_type)?.label}</div>
                      <div className="text-xs text-[hsl(var(--ink-soft))]">
                        {m.pax || 0} pax {m.planned_by && <> · by {m.planned_by}</>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button data-testid={`meal-dup-${m.id}`} onClick={() => openDuplicate(m)} title="Duplicate to another date" className="p-1.5 rounded border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg))]">
                        <Copy size={12} />
                      </button>
                      <button data-testid={`meal-edit-${m.id}`} onClick={() => openEdit(m)} className="p-1.5 rounded border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg))]">
                        <Pencil size={12} />
                      </button>
                      <button data-testid={`meal-del-${m.id}`} onClick={() => remove(m)} className="p-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {(m.items || []).map((it, i) => (
                      <li key={i} className="text-sm flex items-baseline justify-between gap-3 py-1 border-b border-[hsl(var(--rule))] last:border-0">
                        <div className="flex-1">
                          <span className="font-medium">{it.name}</span>
                          {it.section && <span className="text-xs text-[hsl(var(--ink-soft))]"> · {it.section}</span>}
                          {it.notes && <div className="text-xs italic text-[hsl(var(--ink-soft))]">{it.notes}</div>}
                        </div>
                        {Number(it.portions) > 0 && <span className="text-xs text-[hsl(var(--brass-2))] font-semibold">{it.portions}</span>}
                      </li>
                    ))}
                  </ul>
                  {m.notes && <div className="mt-3 text-xs text-[hsl(var(--ink-soft))] border-l-2 border-[hsl(var(--brass))] pl-2">{m.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <MealFormModal
          form={form} setForm={setForm}
          editing={editing} menuItems={menuItems}
          saving={saving} onSave={save} onClose={close}
          addLineFromMenu={addLineFromMenu} addBlankLine={addBlankLine}
          updateLine={updateLine} removeLine={removeLine}
        />
      )}

      {dupTarget && (
        <div data-testid="meal-duplicate-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closeDuplicate(); }}>
          <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-md p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow">Duplicate meal plan</div>
                <h2 className="mt-1 text-xl">
                  {MEAL_TYPES.find((t) => t.value === dupTarget.meal_type)?.label} · {dupTarget.date}
                </h2>
                <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">{(dupTarget.items || []).length} items · {dupTarget.pax} pax</div>
              </div>
              <button onClick={closeDuplicate} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                To date
                <input type="date" data-testid="meal-dup-date" value={dupDate} onChange={(e) => setDupDate(e.target.value)} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Meal type
                <select data-testid="meal-dup-type" value={dupType} onChange={(e) => setDupType(e.target.value)} className="tbb-inp mt-1 w-full">
                  <option value="">Same ({dupTarget.meal_type})</option>
                  {MEAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={closeDuplicate} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
              <button data-testid="meal-dup-save" onClick={doDuplicate} className="btn-brass !py-2 !px-4 text-sm">Duplicate</button>
            </div>
          </div>
          <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.4rem 0.6rem;font-size:0.85rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
        </div>
      )}
    </div>
  );
}

function MealFormModal({ form, setForm, editing, menuItems, saving, onSave, onClose, addLineFromMenu, addBlankLine, updateLine, removeLine }) {
  const [picker, setPicker] = useState("");
  const filtered = useMemo(() => {
    const q = picker.trim().toLowerCase();
    if (!q) return menuItems.slice(0, 20);
    return menuItems.filter((mi) => (mi.name || "").toLowerCase().includes(q) || (mi.section || "").toLowerCase().includes(q)).slice(0, 30);
  }, [picker, menuItems]);

  return (
    <div data-testid="meal-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={onSave} className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-3xl p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow">{editing === "new" ? "New meal plan" : "Edit meal plan"}</div>
            <h2 className="mt-1 text-2xl">
              {MEAL_TYPES.find((t) => t.value === form.meal_type)?.label} · {form.date}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            Date *
            <input required type="date" data-testid="meal-form-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="tbb-inp mt-1 w-full" />
          </label>
          <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            Meal type *
            <select data-testid="meal-form-type" value={form.meal_type} onChange={(e) => setForm({ ...form, meal_type: e.target.value })} className="tbb-inp mt-1 w-full">
              {MEAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            Pax
            <input type="number" min="0" data-testid="meal-form-pax" value={form.pax} onChange={(e) => setForm({ ...form, pax: e.target.value })} className="tbb-inp mt-1 w-full" placeholder="staff eating this meal" />
          </label>
        </div>

        <div className="mt-5 border border-[hsl(var(--rule))]">
          <div className="p-3 bg-[hsl(var(--bg-2))] border-b border-[hsl(var(--rule))] flex items-center gap-3 flex-wrap">
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--brass-2))]">Items</div>
            <div className="relative">
              <input
                data-testid="meal-item-picker"
                placeholder="Search live menu items…"
                value={picker}
                onChange={(e) => setPicker(e.target.value)}
                className="tbb-inp text-sm w-64"
              />
              {picker && filtered.length > 0 && (
                <div className="absolute z-30 left-0 top-full mt-1 w-80 max-h-64 overflow-auto border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] shadow-lg">
                  {filtered.map((mi) => (
                    <button key={mi.id} type="button" data-testid={`meal-pick-${mi.id}`}
                      onClick={() => { addLineFromMenu(mi); setPicker(""); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--bg-2))] flex items-center justify-between">
                      <span>{mi.name}<span className="text-xs text-[hsl(var(--ink-soft))]"> · {mi.section}</span></span>
                      {mi.price && <span className="text-xs text-[hsl(var(--brass-2))]">{mi.price}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" data-testid="meal-add-blank" onClick={addBlankLine} className="text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--rule))]">+ Off-menu item</button>
            <div className="ml-auto text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              {menuItems.length} live menu items available
            </div>
          </div>
          {form.items.length === 0 ? (
            <div className="p-6 text-center text-sm text-[hsl(var(--ink-soft))]">
              Search &amp; pick items from your live menu, or add an off-menu basic like &ldquo;Steamed rice&rdquo;.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                <tr className="border-b border-[hsl(var(--rule))]">
                  <th className="p-2 text-left w-8">#</th>
                  <th className="p-2 text-left">Item</th>
                  <th className="p-2 text-left w-32">Section</th>
                  <th className="p-2 text-right w-24">Portions</th>
                  <th className="p-2 text-left">Notes</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--rule))]">
                {form.items.map((ln, i) => (
                  <tr key={i} data-testid={`meal-line-${i}`}>
                    <td className="p-2 text-[hsl(var(--ink-soft))]">{i + 1}</td>
                    <td className="p-2"><input value={ln.name} onChange={(e) => updateLine(i, { name: e.target.value })} className="tbb-inp w-full text-sm" placeholder="Item name" /></td>
                    <td className="p-2"><input value={ln.section} onChange={(e) => updateLine(i, { section: e.target.value })} className="tbb-inp w-full text-sm" placeholder="—" /></td>
                    <td className="p-2"><input type="number" min="0" step="0.1" value={ln.portions} onChange={(e) => updateLine(i, { portions: e.target.value })} className="tbb-inp w-full text-sm text-right" /></td>
                    <td className="p-2"><input value={ln.notes} onChange={(e) => updateLine(i, { notes: e.target.value })} className="tbb-inp w-full text-sm" placeholder="e.g. medium spice" /></td>
                    <td className="p-2 text-right"><button type="button" onClick={() => removeLine(i)} className="text-[hsl(6_62%_45%)]"><Trash size={13} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <label className="mt-4 block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Plan notes
          <textarea data-testid="meal-form-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="tbb-inp mt-1 w-full resize-none" placeholder="e.g. Extra rice for the pastry team; dinner will be pushed to 10 pm." />
        </label>

        <div className="mt-5 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
          <button type="submit" data-testid="meal-save" disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">{saving ? "Saving…" : "Save meal plan"}</button>
        </div>
        <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.4rem 0.6rem;font-size:0.85rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
      </form>
    </div>
  );
}
